import { ChevronDown, ChevronRight, RotateCcw } from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { getErrorDetails } from "../../../../app/appUtils";
import AppButton from "../../../../components/ui/AppButton";
import { LoadingSpinner } from "../../../../components/ui/common";
import { Modal } from "../../../../components/ui/Modal";
import { DispatchImportJourney } from "../../@types";
import { useGetDispatchImportBatchByIdQuery } from "../../dispatchApiSlice";
import {
  formatDateRange,
  formatImportAmount,
  formatImportDate,
  ImportSection,
  ImportStatsGrid,
  ImportStatusBadge,
} from "./ImportShared";

interface ImportBatchDetailsModalProps {
  isOpen: boolean;
  importBatchId: string;
  onClose: () => void;
  onReverse: () => void;
}

const JourneyRow: React.FC<{ journey: DispatchImportJourney }> = ({
  journey,
}) => {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const loadings = journey.loadings || [];
  const gross = loadings.reduce((sum, l) => sum + (l.grossIncome || 0), 0);
  const net = loadings.reduce((sum, l) => sum + (l.netBanked || 0), 0);

  return (
    <div className="border border-gray-200 rounded-lg">
      <button
        onClick={() => setExpanded((prev) => !prev)}
        className="w-full flex flex-wrap items-center justify-between gap-2 p-3 text-left hover:bg-gray-50"
      >
        <div className="flex items-center gap-2">
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-pColor" />
          ) : (
            <ChevronRight className="w-4 h-4 text-pColor" />
          )}
          <div>
            <p className="text-[14px] font-medium text-hColor">
              {journey.dispatchNumber}
              {journey.isCombined && (
                <span className="ml-2 text-[11px] text-primary bg-primary/10 rounded-full px-2 py-0.5">
                  Combined
                </span>
              )}
            </p>
            <p className="text-[12px] text-pColor">
              {journey.licensePlate} · {formatImportDate(journey.tripDate)} ·{" "}
              {loadings.length} loading(s)
            </p>
          </div>
        </div>
        <div className="text-right text-[13px]">
          <p className="text-green-600">Gross {formatImportAmount(gross)}</p>
          <p className="text-green-700">Net {formatImportAmount(net)}</p>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-200 p-3 space-y-3 bg-gray-50">
          {loadings.map((loading) => (
            <div
              key={`${loading.loadingNumber}-${loading.sourceRowNumber}`}
              className="bg-white border border-gray-200 rounded-lg p-3 text-[13px] space-y-2"
            >
              <div className="flex flex-wrap justify-between gap-2">
                <p className="font-medium text-hColor">
                  Loading {loading.loadingNumber}
                </p>
                <p className="text-pColor">Row {loading.sourceRowNumber}</p>
              </div>
              <p className="text-hColor">
                {loading.terminal}
                {loading.terminalAsEntered &&
                  loading.terminalAsEntered !== loading.terminal && (
                    <span className="text-pColor">
                      {" "}
                      (entered as "{loading.terminalAsEntered}")
                    </span>
                  )}{" "}
                → {loading.destination}
              </p>
              {loading.isCombined && loading.combinedWith && (
                <p className="text-pColor">
                  Combined with {loading.combinedWith}
                </p>
              )}
              <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1">
                {[
                  ["Departure", loading.departureTime || "-"],
                  ["Distance", `${loading.kmCovered ?? 0} km`],
                  ["Passengers", loading.passengers ?? 0],
                  ["Fuel", `${formatImportAmount(loading.fuelAmount)} (${loading.litres ?? 0} L)`],
                  ["Gross income", formatImportAmount(loading.grossIncome)],
                  ["Expenses", formatImportAmount(loading.totalExpenses)],
                  ["Net banked", formatImportAmount(loading.netBanked)],
                ].map(([label, value]) => (
                  <div key={label as string}>
                    <dt className="text-[11px] text-pColor">{label}</dt>
                    <dd className="text-hColor">{value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
          {journey.dispatchId && (
            <button
              onClick={() =>
                navigate(`/admin/dispatch-details/${journey.dispatchId}`)
              }
              className="text-[13px] text-primary font-medium hover:underline"
            >
              View dispatch details
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const ImportBatchDetailsModal: React.FC<ImportBatchDetailsModalProps> = ({
  isOpen,
  importBatchId,
  onClose,
  onReverse,
}) => {
  const { data, isLoading, error } =
    useGetDispatchImportBatchByIdQuery(importBatchId);

  const batch = data?.data?.batch;
  const journeys = data?.data?.journeys || [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Import details"
      className="max-w-4xl"
    >
      <div className="p-4 sm:p-6 space-y-6">
        {isLoading ? (
          <LoadingSpinner />
        ) : error || !batch ? (
          <p className="text-[14px] text-red-600">
            {error
              ? getErrorDetails(error).message
              : "Import batch not found."}
          </p>
        ) : (
          <>
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-[15px] font-semibold text-hColor break-all">
                    {batch.fileName}
                  </p>
                  <ImportStatusBadge status={batch.status} />
                </div>
                <p className="text-[13px] text-pColor">
                  {batch.sheetName && `Sheet: ${batch.sheetName} · `}
                  Ran {formatImportDate(batch.ranOn)}
                  {batch.ranBy && ` by ${batch.ranBy}`}
                  {batch.reversedOn &&
                    ` · Reversed ${formatImportDate(batch.reversedOn)}`}
                </p>
                <p className="text-[13px] text-pColor">
                  Trip dates:{" "}
                  {formatDateRange(batch.tripDateFrom, batch.tripDateTo)}
                </p>
              </div>
              {batch.canBeReversed && (
                <AppButton
                  variant="danger"
                  appearance="outline"
                  size="sm"
                  icon={<RotateCcw className="w-4 h-4" />}
                  onClick={onReverse}
                >
                  Reverse import
                </AppButton>
              )}
            </div>

            <ImportStatsGrid
              stats={[
                { label: "Rows read", value: batch.rowsRead },
                { label: "Rows accepted", value: batch.rowsAccepted },
                { label: "Journeys found", value: batch.journeysFound },
                {
                  label: "Journeys imported",
                  value: batch.journeysImported,
                  tone: "success",
                },
                {
                  label: "Journeys skipped",
                  value: batch.journeysSkipped,
                  tone: batch.journeysSkipped ? "warning" : "default",
                },
                {
                  label: "Journeys failed",
                  value: batch.journeysFailed,
                  tone: batch.journeysFailed ? "danger" : "default",
                },
                {
                  label: "Gross income (NGN)",
                  value: formatImportAmount(batch.grossIncome),
                },
                {
                  label: "Net banked (NGN)",
                  value: formatImportAmount(batch.netBanked),
                },
              ]}
            />

            <ImportSection title="Journeys" count={journeys.length}>
              {journeys.length === 0 ? (
                <p className="text-[13px] text-pColor">
                  No journeys recorded for this import.
                </p>
              ) : (
                <div className="space-y-2">
                  {journeys.map((journey) => (
                    <JourneyRow
                      key={journey.dispatchId || journey.dispatchNumber}
                      journey={journey}
                    />
                  ))}
                </div>
              )}
            </ImportSection>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ImportBatchDetailsModal;
